import { z } from 'zod';
import { PropertyCategorySchema, type PropertyCategory } from '../common/property-taxonomy.schema';
import {
  FactKeyDetailsSchema,
  FactKeyFrequencySchema,
  FactKeyGroupSchema,
  FactKeyNeedCellSchema,
  FactKeyOriginSchema,
  FactKeyProductSchema,
  FactKeyStatusSchema,
  FactValueTypeSchema,
} from './admin-fact-keys.schemas';

/** Every catalog result stays below this many characters of JSON on the seeded catalog. Test-enforced. */
export const FACT_KEY_CATALOG_RESULT_MAX_CHARS = 60_000;

/** Entries per group-details page. A tuning constant: lowered if the size test fails, never part of the shape. */
export const FACT_KEY_GROUP_PAGE_SIZE = 15;

/** The overview's row layout. Every row has exactly these cells, as strings, in this order. */
export const FACT_KEY_CATALOG_OVERVIEW_COLUMNS = [
  'id',
  'label',
  'group',
  'valueType',
  'frequency',
  'objectTypes',
  'needs',
  'origin',
  'status',
] as const;

/** One character per object type, in PROPERTY_CATEGORIES order. `-` marks an absent type. */
export const FACT_KEY_OBJECT_TYPE_CODES = {
  APARTMENT: 'A',
  HOUSE: 'H',
  LAND: 'L',
  COMMERCIAL: 'C',
  GARAGE_PARKING: 'G',
} as const satisfies Record<PropertyCategory, string>;

/** One character per need, the object profile's own notation. */
export const FACT_KEY_NEED_CODES = { P: 'REQUIRED', E: 'RECOMMENDED', O: 'OPTIONAL', '-': 'NOT_USED' } as const;

export const FactKeyCatalogOverviewSchema = z.object({
  catalogVersion: z.string(),
  groups: z.array(FactKeyGroupSchema),
  columns: z.array(z.enum(FACT_KEY_CATALOG_OVERVIEW_COLUMNS)),
  legend: z.object({
    objectTypes: z.array(z.object({ code: z.string().length(1), objectType: PropertyCategorySchema })),
    products: z.array(FactKeyProductSchema),
    needs: z.object({
      P: z.literal('REQUIRED'),
      E: z.literal('RECOMMENDED'),
      O: z.literal('OPTIONAL'),
      '-': z.literal('NOT_USED'),
    }),
  }),
  entries: z.array(z.array(z.string())),
});

export const FactKeyCatalogEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  group: FactKeyGroupSchema,
  valueType: FactValueTypeSchema,
  frequency: FactKeyFrequencySchema,
  objectTypes: z.array(PropertyCategorySchema),
  needs: z.array(FactKeyNeedCellSchema),
  origin: FactKeyOriginSchema,
  status: FactKeyStatusSchema,
  details: FactKeyDetailsSchema.nullable(),
});

export const FactKeyCatalogGroupSchema = z.object({
  catalogVersion: z.string(),
  group: FactKeyGroupSchema,
  page: z.number().int().min(1),
  pageCount: z.number().int().min(1),
  entryCount: z.number().int().min(0),
  entries: z.array(FactKeyCatalogEntrySchema).max(FACT_KEY_GROUP_PAGE_SIZE),
});

export type FactKeyCatalogOverview = z.infer<typeof FactKeyCatalogOverviewSchema>;
export type FactKeyCatalogEntry = z.infer<typeof FactKeyCatalogEntrySchema>;
export type FactKeyCatalogGroup = z.infer<typeof FactKeyCatalogGroupSchema>;

export const FactKeyCatalogGroupPathParamsSchema = z.object({ group: z.string().trim().min(1) });

export const FactKeyCatalogGroupQuerySchema = z.object({ page: z.coerce.number().int().min(1).optional() });
